import type { GetServerSideProps } from 'next';
import { getServerSession } from 'next-auth';
import getConfig from 'next/config';
import { NextSeo } from 'next-seo';

import { createClient } from '../../prismicio';
import { ParsedUrlQuery } from 'querystring';

import type { HubPageResponse } from '../../api/types/hubpageApiTypes';
import HubPage from '../../components/HubPages/HubPage';
import { DefaultLayout } from '../../components/Layouts';
import { nextAuthOptions } from '../../helpers/nextAuthOptions';
import Error from '../../pages/_error';
import { generateOpenGraph } from '../../helpers/seo/hubPageSeo';
import { PAGE } from '../../helpers/pages';

import { isOk, isError } from '../../domains/Result';

import { orderRepository } from '../../repositories/Order/OrderRepository';

import { HISTORY_CHECK_REPORT } from '../../components/Toolkit/HistoryCheckModal/HistoryCheckModal.typed';
import { formatAuthorizationHeader } from '../../helpers/auth';
import { logger } from '@dist-marketplace-frontend/services';
import { useVerticalContext } from '../../contexts/VerticalContext';
import { useEffect } from 'react';
import { VERTICAL } from '../../helpers/verticals';

interface Params extends ParsedUrlQuery {
  hub: string;
}

function Hub(props: HubPageResponse) {
  const {
    publicRuntimeConfig: { ENVIRONMENT_NAME, CDN_STATIC_ASSETS },
  } = getConfig();

  const {
    domain = '',
    meta_description,
    meta_name,
    og_image,
    pageName,
  } = props;

  const shouldUseGenericLogoInHeader =
    getVerticalFromPageName(pageName) === null;

  const { updateVertical } = useVerticalContext();

  useEffect(() => {
    const vertical = getVerticalFromPageName(pageName);
    if (vertical) {
      updateVertical(vertical);
    }
  }, [updateVertical, pageName]);

  if (props.type === 'error') {
    return <Error statusCode={404} disableGtmLog={true} domain={domain} />;
  }

  const openGraph = {
    domain,
    pageName,
    title: meta_name[0].text,
    description: meta_description[0].text,
    image: og_image.url,
  };

  return (
    <DefaultLayout
      useGenericLogoInHeader={shouldUseGenericLogoInHeader}
      domain={domain}
      headerVariant="TRANSPARENT"
    >
      <NextSeo
        title={meta_name[0]?.text}
        description={meta_description[0]?.text}
        nofollow={ENVIRONMENT_NAME !== 'production'}
        openGraph={generateOpenGraph(openGraph)}
      />
      <HubPage {...props} baseUrl={domain} cdnUrl={CDN_STATIC_ASSETS} />
    </DefaultLayout>
  );
}

function getVerticalFromPageName(pageName: string): VERTICAL | null {
  switch (pageName) {
    case 'electric-cars':
    case 'instant-offer':
    case 'sell-my-car':
    case 'instant-offer-autoguru':
    case 'greenlight-history-check':
    case 'finance':
    case 'reserve-online':
    case 'free-history-check':
      return VERTICAL.MOTOR;
    case 'agri-hub':
      return VERTICAL.FARMING;
    case 'content-test':
    default:
      return null;
  }
}

export const getServerSideProps: GetServerSideProps<any, Params> = async (
  ctx,
) => {
  try {
    const params = ctx.params!;
    const query = ctx.query;
    const previewData = ctx.previewData;
    const { hub } = params;

    const isHistoryCheckPage = hub === 'greenlight-history-check';

    if (isHistoryCheckPage) {
      const historyCheckRequired = query.api_request === 'history-check';
      const registration = query.registration as string;
      const invokeHistoryCheckApi = historyCheckRequired && registration;

      if (invokeHistoryCheckApi) {
        const session = await getServerSession(
          ctx.req,
          ctx.res,
          nextAuthOptions,
        );

        const historyReport = await orderRepository.historyReport(
          {
            type: HISTORY_CHECK_REPORT.STANDALONE_COMPLETE_REPORT,
            param: registration,
          },
          formatAuthorizationHeader(session?.accessToken),
        );

        if (isOk(historyReport)) {
          const orderID = historyReport.ok.id;
          return {
            redirect: {
              permanent: false,
              destination: `/order/${orderID}`,
            },
          };
        }

        if (isError(historyReport)) {
          const error = historyReport.error;
          logger.error({
            route: PAGE.HUB,
            error,
            message: 'Error getting history report',
          });
        }
      }
    }

    // Prismic setup
    const client = createClient({ previewData });
    // There's a mismatch in prismicio's typings so we need to cast here to properly avoid errors.
    // The issue is with an outdated version of prismicio's types being included via the
    // prismicio/react package which has a conflicting timestamp field.
    const page = await client.getByUID('hub_pages', hub);

    const data = page.data;

    return {
      props: { ...data, pageName: hub, type: 'success' },
    };
  } catch (error) {
    logger.error({
      route: PAGE.HUB,
      error,
      message: 'Unknown server error',
    });
    return {
      props: {
        type: 'error',
      },
    };
  }
};

export default Hub;
